import { getCallbackUrl, getNativeAuthCallbackUrl } from './environment';

const MAX_CALLBACK_FINGERPRINTS = 8;
const FNV_OFFSET_BASIS_64 = 14_695_981_039_346_656_037n;
const FNV_PRIME_64 = 1_099_511_628_211n;

type CallbackFingerprintState = 'active' | 'handled';

const callbackFingerprintStates = new Map<string, CallbackFingerprintState>();
let pendingCallbackFingerprint: string | null = null;

function matchesCallbackTarget(rawUrl: string, expectedUrl: string): boolean {
  let parsed: URL;
  let expected: URL;
  try {
    parsed = new URL(rawUrl);
    expected = new URL(expectedUrl);
  } catch {
    return false;
  }

  if (parsed.username || parsed.password || parsed.port) {
    return false;
  }
  if (expected.protocol === 'https:') {
    return parsed.origin === expected.origin && parsed.pathname === expected.pathname;
  }
  return (
    parsed.protocol === expected.protocol &&
    parsed.hostname === expected.hostname &&
    parsed.pathname === expected.pathname
  );
}

function fingerprintCallbackUrl(url: string): string {
  let hash = FNV_OFFSET_BASIS_64;
  for (const character of url) {
    hash ^= BigInt(character.codePointAt(0) ?? 0);
    hash = BigInt.asUintN(64, hash * FNV_PRIME_64);
  }
  return `${url.length}:${hash.toString(16)}`;
}

function trimCallbackFingerprints(): void {
  while (callbackFingerprintStates.size > MAX_CALLBACK_FINGERPRINTS) {
    const oldest = [...callbackFingerprintStates.keys()][0];
    if (oldest === undefined) {
      return;
    }
    callbackFingerprintStates.delete(oldest);
  }
}

/** Strictly classify only this build environment's native OAuth callback targets. */
export function isNativeOAuthCallbackUrl(url: string): boolean {
  return (
    matchesCallbackTarget(url, getNativeAuthCallbackUrl()) ||
    matchesCallbackTarget(url, getCallbackUrl())
  );
}

/** Claim a one-time callback without retaining its URL or authorization code. */
export function claimNativeOAuthCallbackUrl(url: string): boolean {
  if (!isNativeOAuthCallbackUrl(url)) {
    return false;
  }
  const fingerprint = fingerprintCallbackUrl(url);
  if (callbackFingerprintStates.has(fingerprint)) {
    return false;
  }
  callbackFingerprintStates.set(fingerprint, 'active');
  pendingCallbackFingerprint = fingerprint;
  trimCallbackFingerprints();
  return true;
}

export function isNativeOAuthCallbackUrlHandled(url: string): boolean {
  return callbackFingerprintStates.get(fingerprintCallbackUrl(url)) === 'handled';
}

export function takeClaimedNativeOAuthCallbackFingerprint(): string | null {
  const fingerprint = pendingCallbackFingerprint;
  pendingCallbackFingerprint = null;
  return fingerprint;
}

export function markNativeOAuthCallbackHandled(fingerprint: string | null): void {
  if (!fingerprint) {
    return;
  }
  callbackFingerprintStates.set(fingerprint, 'handled');
  trimCallbackFingerprints();
}

export function __resetNativeAuthCallbackUrlsForTests(): void {
  pendingCallbackFingerprint = null;
  callbackFingerprintStates.clear();
}
